import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Firm AI review policy — the "auto-approve AI proposals" switch and the
 * history of what the system approved under it.
 *
 *   GET /api/v1/admin/firm/ai-review-policy                 (any firm user)
 *   PUT /api/v1/admin/firm/ai-review-policy                 (firm admin)
 *   GET /api/v1/admin/firm/ai-review-policy/auto-approved   (firm admin)
 */
export const AiReviewPolicySchema = z.object({
  auto_approve_proposals: z.boolean(),
  updated_at: z.string().nullable(),
  updated_by_user_id: z.string().nullable(),
  updated_by_name: z.string().nullable(),
});
export type AiReviewPolicy = z.infer<typeof AiReviewPolicySchema>;

export const AutoApprovedItemSchema = z.object({
  id: z.string(),
  engagement_id: z.string(),
  engagement_name: z.string(),
  proposal_type: z.string(),
  proposed_value: z.unknown(),
  confidence: z.number(),
  decision_at: z.string().nullable(),
  committed_register_entry_id: z.string().nullable(),
});
export type AutoApprovedItem = z.infer<typeof AutoApprovedItemSchema>;

const HistorySchema = z.object({ items: z.array(AutoApprovedItemSchema) });

const POLICY_PATH = '/api/v1/admin/firm/ai-review-policy';
const POLICY_KEY = ['ai-review-policy'] as const;

export function useAiReviewPolicyQuery() {
  return useQuery({
    queryKey: POLICY_KEY,
    queryFn: () => apiClient(POLICY_PATH, AiReviewPolicySchema),
  });
}

export function useAutoApprovedHistoryQuery(enabled = true) {
  return useQuery({
    queryKey: [...POLICY_KEY, 'auto-approved'] as const,
    queryFn: () => apiClient(`${POLICY_PATH}/auto-approved`, HistorySchema),
    enabled,
  });
}

export function useSetAiReviewPolicyMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (autoApprove: boolean) =>
      apiClient(POLICY_PATH, AiReviewPolicySchema, {
        method: 'PUT',
        body: { auto_approve_proposals: autoApprove },
      }),
    onSuccess: (policy) => {
      qc.setQueryData(POLICY_KEY, policy);
    },
  });
}
